import { ENDPOINTS } from "utils/endpoints";
import { GET_API_CALL } from "utils/services";
import {
  FeedbackListQuery,
  FeedbackListResult,
  FeedbackPerson,
  FeedbackRow,
} from "./types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toNumber = (value: unknown) => {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toText = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const toOptionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const mapFeedbackImages = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => toText(item))
      .filter(Boolean);
  }
  // Older payloads returned a single URL string.
  const single = toText(value);
  return single ? [single] : [];
};

const mapPerson = (value: unknown): FeedbackPerson => {
  const source = isRecord(value) ? value : {};
  return {
    id: toText(source._id || source.id),
    name: toText(source.name),
    phone: toText(source.phone),
    image: toText(source.image),
  };
};

export const mapFeedbackRow = (value: unknown): FeedbackRow | null => {
  if (!isRecord(value)) return null;
  const service = isRecord(value.service) ? value.service : {};
  const order = isRecord(value.order) ? value.order : {};

  return {
    id: toText(value._id || value.id),
    feedback: toText(value.feedback),
    rating: toOptionalNumber(value.rating),
    createdAt: toText(value.createdAt),
    feedbackImages: mapFeedbackImages(
      value.feedbackImage ?? value.feedbackImages
    ),
    type: toText(value.type),
    order: {
      id: toText(order._id || order.id),
      orderId: toText(order.orderId),
    },
    user: mapPerson(value.user),
    merchant: mapPerson(value.merchant),
    servicePartner: mapPerson(value.servicePartner),
    service: {
      id: toText(service._id || service.id),
      name: toText(service.serviceName || service.name),
      phone: "",
      image: toText(service.image),
    },
  };
};

export const mapFeedbackList = (value: unknown): FeedbackListResult => {
  const source = isRecord(value) ? value : {};
  const rawRows = Array.isArray(source.data) ? source.data : [];

  return {
    rows: rawRows
      .map(mapFeedbackRow)
      .filter((row): row is FeedbackRow => row !== null),
    total: toNumber(source.total),
    page: toNumber(source.page) || 1,
    limit: toNumber(source.limit) || 10,
  };
};

const buildQuery = (query: FeedbackListQuery) => {
  const params = new URLSearchParams();
  params.set("page", `${query.page}`);
  params.set("limit", `${query.limit}`);
  if (query.type) params.set("type", query.type);
  if (query.category !== "") params.set("category", query.category);
  return `?${params.toString()}`;
};

export const getFeedbackList = (
  query: FeedbackListQuery,
  callback: (result: FeedbackListResult | null) => void
) => {
  GET_API_CALL(
    ENDPOINTS.FEEDBACK.LIST,
    buildQuery(query),
    (response: { isSuccess?: boolean; data?: unknown }) => {
      if (!response?.isSuccess) {
        callback(null);
        return;
      }
      callback(mapFeedbackList(response.data));
    }
  );
};
